import React, { useState, useEffect } from 'react';
import { Card, Nav, Form, Row, Col, Table, Button, Spinner, Alert, InputGroup } from 'react-bootstrap';
import { FaUpload, FaSave, FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as solidIcons from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import './AboutManagement.scss';

const AboutManagement = () => {
  const [key, setKey] = useState('about');
  
  // Loading, error, and success states
  const [loading, setLoading] = useState({
    about: false,
    values: false,
    team: false,
    teamMember: false,
    testimonials: false,
    testimonialItem: false
  });
  
  const [error, setError] = useState({
    about: null,
    values: null,
    team: null,
    teamMember: null,
    testimonials: null,
    testimonialItem: null
  });
  
  const [success, setSuccess] = useState({
    about: null,
    values: null,
    team: null,
    teamMember: null,
    testimonials: null,
    testimonialItem: null
  });
  
  // Data states
  const [aboutData, setAboutData] = useState({
    header: 'About Our Company',
    paragraph: 'We are a leading real estate company committed to excellence.',
    img: 'https://via.placeholder.com/800x400?text=About+Section'
  });

  const [valuesData, setValuesData] = useState({
    header: 'Our Core Values',
    paragraph: 'Integrity, Excellence, and Customer Satisfaction drive everything we do.',
    img: 'https://via.placeholder.com/800x400?text=Values+Section',
    values: [] // Initialize values as an empty array
  });

  const [teamData, setTeamData] = useState({
    header: 'Our Expert Team',
    paragraph: 'Meet our dedicated professionals who make it all happen.',
    members: [
      {
        id: 1,
        name: 'John Smith',
        position: 'CEO',
        paragraph: 'Over 15 years of experience in real estate.',
        img: 'https://via.placeholder.com/150x150?text=John+Smith'
      },
      {
        id: 2,
        name: 'Sarah Johnson',
        position: 'Sales Director',
        paragraph: 'Expert in luxury property sales.',
        img: 'https://via.placeholder.com/150x150?text=Sarah+Johnson'
      }
    ]
  });

  const [testimonialData, setTestimonialData] = useState({
    header: 'Client Testimonials',
    paragraph: 'What our satisfied clients say about us.',
    testimonials: [
      {
        id: 1,
        name: 'Michael Brown',
        position: 'Property Investor',
        paragraph: 'Outstanding service and professional team.',
        img: 'https://via.placeholder.com/150x150?text=Michael+Brown'
      },
      {
        id: 2,
        name: 'Emma Wilson',
        position: 'Homeowner',
        paragraph: 'Made my dream home a reality.',
        img: 'https://via.placeholder.com/150x150?text=Emma+Wilson'
      }
    ]
  });

  // Fetch all about page data
  useEffect(() => {
    fetchAboutData();
  }, []);

  const fetchAboutData = async () => {
    try {
      setLoading(prev => ({ ...prev, about: true, values: true, team: true, testimonials: true }));
      setError(prev => ({ ...prev, about: null, values: null, team: null, testimonials: null }));
      
      const response = await axios.get('/api/about');
      
      if (response.data.status === 'success') {
        const { hero, values, team, testimonials } = response.data.data;
        
        // Update hero section data
        if (hero) {
          setAboutData({
            header: hero.header || 'About Our Company',
            paragraph: hero.paragraph || 'We are a leading real estate company committed to excellence.',
            img: hero.image_path ? `/api/image/${hero.image_path.replace(/\\/g, '/')}` : 'https://via.placeholder.com/800x400?text=About+Section'
          });
        }
        
        // Update values section data
        if (values) {
          // Ensure values.values is an array
          let valuesArray = values.values || [];
          
          // If it's a string (which shouldn't happen with Laravel's array casting), parse it
          if (typeof valuesArray === 'string') {
            try {
              valuesArray = JSON.parse(valuesArray);
            } catch (e) {
              console.error('Error parsing values from API:', e);
              valuesArray = [];
            }
          }
          
          // Final check to ensure it's an array
          if (!Array.isArray(valuesArray)) {
            console.error('Values from API is not an array after processing:', valuesArray);
            valuesArray = [];
          }
          
          setValuesData({
            header: values.header || 'Our Core Values',
            paragraph: values.paragraph || 'Integrity, Excellence, and Customer Satisfaction drive everything we do.',
            values: valuesArray
          });
        }
        
        // Update team section data
        if (team) {
          setTeamData({
            header: team.header || 'Our Expert Team',
            paragraph: team.paragraph || 'Meet our dedicated professionals who make it all happen.',
            members: team.members?.map(member => ({
              id: member.id,
              name: member.name,
              position: member.position,
              paragraph: member.paragraph,
              img: member.image_path ? `/api/image/${member.image_path.replace(/\\/g, '/')}` : 'https://via.placeholder.com/150x150?text=Team+Member'
            })) || []
          });
        }
        
        // Update testimonials section data
        if (testimonials) {
          setTestimonialData({
            header: testimonials.header || 'Client Testimonials',
            paragraph: testimonials.paragraph || 'What our satisfied clients say about us.',
            testimonials: testimonials.testimonials?.map(item => ({
              id: item.id,
              name: item.name,
              position: item.position,
              paragraph: item.paragraph,
              img: item.image_path ? `/api/image/${item.image_path.replace(/\\/g, '/')}` : 'https://via.placeholder.com/150x150?text=Testimonial'
            })) || []
          });
        }
      }
    } catch (error) {
      console.error('Error fetching about page data:', error);
      setError(prev => ({
        ...prev,
        about: 'Failed to load about page data',
        values: 'Failed to load values data',
        team: 'Failed to load team data',
        testimonials: 'Failed to load testimonials data'
      }));
    } finally {
      setLoading(prev => ({ ...prev, about: false, values: false, team: false, testimonials: false }));
    }
  };

  const handleImageUpload = async (section, id = null, e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Initialize variables outside try block to ensure they're available in catch and finally blocks
    let endpoint = '';
    let loadingKey = section;
    let successKey = section;
    
    try {
      // Set loading state
      setLoading(prev => ({ ...prev, [loadingKey]: true }));
      setError(prev => ({ ...prev, [loadingKey]: null }));
      setSuccess(prev => ({ ...prev, [loadingKey]: null }));
      
      if (section === 'team' && id) {
        // Special handling for team member images using dedicated image upload endpoint
        loadingKey = 'teamMember';
        successKey = 'teamMember';
        
        const member = teamData.members.find(m => m.id === id);
        console.log('Team member data for image upload:', member);
        
        if (!member) {
          throw new Error('Team member not found');
        }
        
        // Create a simple FormData with just the image
        const imageFormData = new FormData();
        imageFormData.append('image', file);
        
        console.log('Uploading image to dedicated endpoint');
        
        // Use the dedicated image upload endpoint
        const response = await axios.post(`/api/about/team/member/${id}/image`, imageFormData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (response.data.status === 'success') {
          // Update the team data with the new image
          setTeamData({
            ...teamData,
            members: teamData.members.map(m => {
              if (m.id === id) {
                return {
                  ...m,
                  img: response.data.data.image_path ? `/api/image/${response.data.data.image_path.replace(/\\/g, '/')}` : m.img
                };
              }
              return m;
            })
          });
          
          setSuccess(prev => ({
            ...prev,
            [successKey]: 'Team member image updated successfully!'
          }));
          
          setTimeout(() => {
            setSuccess(prev => ({ ...prev, [successKey]: null }));
          }, 5000);
        }
        
        return; // Exit early for team member image uploads
      } else if (section === 'testimonials' && id) {
        // Special handling for testimonial images using dedicated image upload endpoint
        loadingKey = 'testimonialItem';
        successKey = 'testimonialItem';
        
        const testimonial = testimonialData.testimonials.find(t => t.id === id);
        console.log('Testimonial data for image upload:', testimonial);
        
        if (!testimonial) {
          throw new Error('Testimonial not found');
        }
        
        // Create a simple FormData with just the image
        const imageFormData = new FormData();
        imageFormData.append('image', file);
        
        console.log('Uploading image to dedicated endpoint');
        
        // Use the dedicated image upload endpoint
        const response = await axios.post(`/api/about/testimonials/item/${id}/image`, imageFormData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (response.data.status === 'success') {
          // Update the testimonial data with the new image
          setTestimonialData({
            ...testimonialData,
            testimonials: testimonialData.testimonials.map(t => {
              if (t.id === id) {
                return {
                  ...t,
                  img: response.data.data.image_path ? `/api/image/${response.data.data.image_path.replace(/\\/g, '/')}` : t.img
                };
              }
              return t;
            })
          });
          
          setSuccess(prev => ({
            ...prev,
            [successKey]: 'Testimonial image updated successfully!'
          }));
          
          setTimeout(() => {
            setSuccess(prev => ({ ...prev, [successKey]: null }));
          }, 5000);
        }
        
        return; // Exit early for testimonial image uploads
      }
      
      // For other sections (about, values, team section, testimonials section)
      // Create FormData object
      const formData = new FormData();
      formData.append('image', file);
      
      // Set appropriate endpoint and data based on section
      if (section === 'about') {
        endpoint = '/api/about/hero';
        formData.append('header', aboutData.header);
        formData.append('paragraph', aboutData.paragraph);
      } else if (section === 'values') {
        endpoint = '/api/about/values';
        formData.append('header', valuesData.header);
        formData.append('paragraph', valuesData.paragraph);
        
        // Ensure values is an array before sending
        let valuesArray = valuesData.values;
        if (typeof valuesArray === 'string') {
          try {
            valuesArray = JSON.parse(valuesArray);
          } catch (e) {
            console.error('Error parsing values:', e);
            valuesArray = [];
          }
        }
        
        // Make sure valuesArray is actually an array
        if (!Array.isArray(valuesArray)) {
          console.error('Values is not an array before upload:', valuesArray);
          valuesArray = [];
        }
        
        // Log the values array before sending to API for debugging
        console.log('Values array before image upload:', valuesArray);
        
        // Important: First append an empty values array to ensure the backend recognizes it as an array
        formData.append('values', '');
        
        // For FormData, we need to handle arrays differently
        // Instead of JSON.stringify, we'll append each value individually
        valuesArray.forEach((value, index) => {
          formData.append(`values[${index}][title]`, value.title || '');
          formData.append(`values[${index}][icon]`, value.icon || '');
          formData.append(`values[${index}][description]`, value.description || '');
        });
      } else if (section === 'team') {
        // Update team section
        endpoint = '/api/about/team';
        formData.append('header', teamData.header);
        formData.append('paragraph', teamData.paragraph);
      } else if (section === 'testimonials') {
        // Update testimonials section
        endpoint = '/api/about/testimonials';
        formData.append('header', testimonialData.header);
        formData.append('paragraph', testimonialData.paragraph);
      }
      
      // Make API request
      const response = await axios.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.status === 'success') {
        // Update state based on section
        if (section === 'about') {
          setAboutData({
            ...aboutData,
            img: response.data.data.image_path ? `/api/image/${response.data.data.image_path.replace(/\\/g, '/')}` : aboutData.img
          });
        } else if (section === 'values') {
          setValuesData({
            ...valuesData,
            img: response.data.data.image_path ? `/api/image/${response.data.data.image_path.replace(/\\/g, '/')}` : valuesData.img
          });
        } else if (section === 'team' && id) {
          setTeamData({
            ...teamData,
            members: teamData.members.map(member => {
              if (member.id === id) {
                return {
                  ...member,
                  img: response.data.data.image_path ? `/api/image/${response.data.data.image_path.replace(/\\/g, '/')}` : member.img
                };
              }
              return member;
            })
          });
        } else if (section === 'testimonials' && id) {
          setTestimonialData({
            ...testimonialData,
            testimonials: testimonialData.testimonials.map(item => {
              if (item.id === id) {
                return {
                  ...item,
                  img: response.data.data.image_path ? `/api/image/${response.data.data.image_path.replace(/\\/g, '/')}` : item.img
                };
              }
              return item;
            })
          });
        }
        
        // Set success message
        setSuccess(prev => ({
          ...prev,
          [successKey]: `Image updated successfully!`
        }));
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccess(prev => ({ ...prev, [successKey]: null }));
        }, 5000);
      }
    } catch (error) {
      console.error(`Error uploading ${section} image:`, error);
      setError(prev => ({
        ...prev,
        [section]: error.response?.data?.message || error.message || `Failed to upload image`
      }));
      
      // Log more detailed error information
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        console.error('Error response headers:', error.response.headers);
      } else if (error.request) {
        // The request was made but no response was received
        console.error('Error request:', error.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Error message:', error.message);
      }
    } finally {
      setLoading(prev => ({ ...prev, [section]: false }));
    }
  };

  const handleSave = async (section) => {
    // Initialize loadingKey and successKey outside the try block to ensure they're available in catch and finally blocks
    let endpoint = '';
    let loadingKey = section;
    let successKey = section;
    
    try {
      // Set loading state
      setLoading(prev => ({ ...prev, [loadingKey]: true }));
      setError(prev => ({ ...prev, [loadingKey]: null }));
      setSuccess(prev => ({ ...prev, [loadingKey]: null }));
      
      // Special handling for values section
      if (section === 'values') {
        endpoint = '/api/about/values';
        
        // Create FormData for values section
        const formData = new FormData();
        formData.append('header', valuesData.header);
        formData.append('paragraph', valuesData.paragraph);
        
        // Ensure values is an array, not a string
        let valuesArray = valuesData.values;
        
        // Handle different data types for values
        if (typeof valuesArray === 'string') {
          try {
            valuesArray = JSON.parse(valuesArray);
          } catch (e) {
            console.error('Error parsing values:', e);
            valuesArray = [];
          }
        }
        
        // Make sure valuesArray is actually an array
        if (!Array.isArray(valuesArray)) {
          console.error('Values is not an array after processing:', valuesArray);
          valuesArray = [];
        }
        
        // Log the values array before sending to API for debugging
        console.log('Values array before sending to API:', valuesArray);
        
        // Important: First append an empty values array to ensure the backend recognizes it as an array
        formData.append('values', '');
        
        // For FormData, we need to handle arrays differently
        // Instead of JSON.stringify, we'll append each value individually
        valuesArray.forEach((value, index) => {
          formData.append(`values[${index}][title]`, value.title || '');
          formData.append(`values[${index}][icon]`, value.icon || '');
          formData.append(`values[${index}][description]`, value.description || '');
        });
        
        // Make API request with FormData
        const response = await axios.post(endpoint, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (response.data.status === 'success') {
          // Set success message
          setSuccess(prev => ({
            ...prev,
            [successKey]: `${section.charAt(0).toUpperCase() + section.slice(1)} section updated successfully!`
          }));
          
          // Clear success message after 5 seconds
          setTimeout(() => {
            setSuccess(prev => ({ ...prev, [successKey]: null }));
          }, 5000);
        }
      } else {
        // Handle other sections with regular JSON data
        let data = {};
        
        // Set appropriate endpoint and data based on section
        if (section === 'about') {
          endpoint = '/api/about/hero';
          data = {
            header: aboutData.header,
            paragraph: aboutData.paragraph
          };
        } else if (section === 'team') {
          endpoint = '/api/about/team';
          
          // First update the team section header and paragraph
          const formData = new FormData();
          formData.append('header', teamData.header);
          formData.append('paragraph', teamData.paragraph);
          
          // Make API request to update team section
          const response = await axios.post(endpoint, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          
          if (response.data.status === 'success') {
            // Now update each team member individually
            let allMembersUpdated = true;
            let updateErrors = [];
            
            // Create an array of promises for updating each team member
            const updatePromises = teamData.members.map(async (member) => {
              try {
                if (!member.id) return; // Skip if no ID (shouldn't happen)
                
                // Use JSON data instead of FormData for the update
                const memberData = {
                  name: member.name,
                  position: member.position,
                  paragraph: member.paragraph
                };
                
                // Make API request to update this team member
                await axios.put(`/api/about/team/member/${member.id}`, memberData);
              } catch (error) {
                console.error(`Error updating team member ${member.id}:`, error);
                allMembersUpdated = false;
                updateErrors.push(`Member ${member.name}: ${error.message}`);
              }
            });
            
            // Wait for all update requests to complete
            await Promise.all(updatePromises);
            
            // Set appropriate success or error message
            if (allMembersUpdated) {
              setSuccess(prev => ({
                ...prev,
                [successKey]: `${section.charAt(0).toUpperCase() + section.slice(1)} section and all members updated successfully!`
              }));
            } else {
              setSuccess(prev => ({
                ...prev,
                [successKey]: `${section.charAt(0).toUpperCase() + section.slice(1)} section updated but some members failed: ${updateErrors.join(', ')}`
              }));
            }
            
            // Clear success message after 5 seconds
            setTimeout(() => {
              setSuccess(prev => ({ ...prev, [successKey]: null }));
            }, 5000);
          }
          
          // Skip the regular JSON data handling for team section
          return;
        } else if (section === 'testimonials') {
          endpoint = '/api/about/testimonials';
          
          // First update the testimonials section header and paragraph
          const formData = new FormData();
          formData.append('header', testimonialData.header);
          formData.append('paragraph', testimonialData.paragraph);
          
          // Make API request to update testimonials section
          const response = await axios.post(endpoint, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          
          if (response.data.status === 'success') {
            // Now update each testimonial individually
            let allTestimonialsUpdated = true;
            let updateErrors = [];
            
            // Create an array of promises for updating each testimonial
            const updatePromises = testimonialData.testimonials.map(async (testimonial) => {
              try {
                if (!testimonial.id) return; // Skip if no ID (shouldn't happen)
                
                // Use JSON data instead of FormData for the update
                const testimonialItemData = {
                  name: testimonial.name,
                  position: testimonial.position,
                  paragraph: testimonial.paragraph
                };
                
                // Make API request to update this testimonial
                await axios.put(`/api/about/testimonials/item/${testimonial.id}`, testimonialItemData);
              } catch (error) {
                console.error(`Error updating testimonial ${testimonial.id}:`, error);
                allTestimonialsUpdated = false;
                updateErrors.push(`Testimonial ${testimonial.name}: ${error.message}`);
              }
            });
            
            // Wait for all update requests to complete
            await Promise.all(updatePromises);
            
            // Set appropriate success or error message
            if (allTestimonialsUpdated) {
              setSuccess(prev => ({
                ...prev,
                [successKey]: `${section.charAt(0).toUpperCase() + section.slice(1)} section and all testimonials updated successfully!`
              }));
            } else {
              setSuccess(prev => ({
                ...prev,
                [successKey]: `${section.charAt(0).toUpperCase() + section.slice(1)} section updated but some testimonials failed: ${updateErrors.join(', ')}`
              }));
            }
            
            // Clear success message after 5 seconds
            setTimeout(() => {
              setSuccess(prev => ({ ...prev, [successKey]: null }));
            }, 5000);
          }
          
          // Skip the regular JSON data handling for testimonials section
          return;
        }
        
        // Make API request
        const response = await axios.post(endpoint, data);
      
        if (response.data.status === 'success') {
          // Set success message
          setSuccess(prev => ({
            ...prev,
            [successKey]: `${section.charAt(0).toUpperCase() + section.slice(1)} section updated successfully!`
          }));
          
          // Clear success message after 5 seconds
          setTimeout(() => {
            setSuccess(prev => ({ ...prev, [successKey]: null }));
          }, 5000);
        }
      }
    } catch (error) {
      console.error(`Error saving ${section} data:`, error);
      setError(prev => ({
        ...prev,
        [section]: error.response?.data?.message || error.message || `Failed to update ${section} section`
      }));
      
      // Log more detailed error information
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        console.error('Error response headers:', error.response.headers);
      } else if (error.request) {
        // The request was made but no response was received
        console.error('Error request:', error.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Error message:', error.message);
      }
    } finally {
      setLoading(prev => ({ ...prev, [loadingKey]: false }));
    }
  };

  const handleAddItem = async (section) => {
    // Initialize variables outside try block to ensure they're available in catch and finally blocks
    let endpoint = '';
    let formData = new FormData();
    let loadingKey = section === 'team' ? 'teamMember' : 'testimonialItem';
    
    try {
      
      // Set loading state
      setLoading(prev => ({ ...prev, [loadingKey]: true }));
      setError(prev => ({ ...prev, [loadingKey]: null }));
      setSuccess(prev => ({ ...prev, [loadingKey]: null }));
      
      // Set appropriate endpoint and data
      if (section === 'team') {
        endpoint = '/api/about/team/member';
        formData.append('name', 'New Team Member');
        formData.append('position', 'Position');
        formData.append('paragraph', 'Description goes here.');
      } else {
        endpoint = '/api/about/testimonials/item';
        formData.append('name', 'New Client');
        formData.append('position', 'Client');
        formData.append('paragraph', 'Testimonial text goes here.');
      }
      
      // Make API request
      const response = await axios.post(endpoint, formData);
      
      if (response.data.status === 'success') {
        // Update state based on section
        if (section === 'team') {
          const newMember = {
            id: response.data.data.id,
            name: response.data.data.name,
            position: response.data.data.position,
            paragraph: response.data.data.paragraph,
            img: response.data.data.image_path ? 
              `/api/image/${response.data.data.image_path.replace(/\\/g, '/')}` : 
              'https://via.placeholder.com/150x150?text=Team+Member'
          };
          
          setTeamData({
            ...teamData,
            members: [...teamData.members, newMember]
          });
        } else {
          const newTestimonial = {
            id: response.data.data.id,
            name: response.data.data.name,
            position: response.data.data.position,
            paragraph: response.data.data.paragraph,
            img: response.data.data.image_path ? 
              `/api/image/${response.data.data.image_path.replace(/\\/g, '/')}` : 
              'https://via.placeholder.com/150x150?text=Testimonial'
          };
          
          setTestimonialData({
            ...testimonialData,
            testimonials: [...testimonialData.testimonials, newTestimonial]
          });
        }
        
        // Set success message
        setSuccess(prev => ({
          ...prev,
          [loadingKey]: section === 'team' ? 'Team member added successfully!' : 'Testimonial added successfully!'
        }));
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccess(prev => ({ ...prev, [loadingKey]: null }));
        }, 5000);
      }
    } catch (error) {
      console.error(`Error adding ${section === 'team' ? 'team member' : 'testimonial'}:`, error);
      setError(prev => ({
        ...prev,
        [section === 'team' ? 'teamMember' : 'testimonialItem']: 
          error.response?.data?.message || error.message || `Failed to add ${section === 'team' ? 'team member' : 'testimonial'}`
      }));
      
      // Log more detailed error information
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        console.error('Error response headers:', error.response.headers);
      } else if (error.request) {
        // The request was made but no response was received
        console.error('Error request:', error.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Error message:', error.message);
      }
    } finally {
      setLoading(prev => ({ 
        ...prev, 
        [section === 'team' ? 'teamMember' : 'testimonialItem']: false 
      }));
    }
  };

  const handleDeleteItem = async (section, id) => {
    // Initialize variables outside try block to ensure they're available in catch and finally blocks
    let endpoint = '';
    let loadingKey = section === 'team' ? 'teamMember' : 'testimonialItem';
    
    try {
      
      // Set loading state
      setLoading(prev => ({ ...prev, [loadingKey]: true }));
      setError(prev => ({ ...prev, [loadingKey]: null }));
      setSuccess(prev => ({ ...prev, [loadingKey]: null }));
      
      // Set appropriate endpoint
      if (section === 'team') {
        endpoint = `/api/about/team/member/${id}`;
      } else {
        endpoint = `/api/about/testimonials/item/${id}`;
      }
      
      // Make API request
      const response = await axios.delete(endpoint);
      
      if (response.data.status === 'success') {
        // Update state based on section
        if (section === 'team') {
          setTeamData({
            ...teamData,
            members: teamData.members.filter(member => member.id !== id)
          });
        } else {
          setTestimonialData({
            ...testimonialData,
            testimonials: testimonialData.testimonials.filter(testimonial => testimonial.id !== id)
          });
        }
        
        // Set success message
        setSuccess(prev => ({
          ...prev,
          [loadingKey]: section === 'team' ? 'Team member deleted successfully!' : 'Testimonial deleted successfully!'
        }));
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccess(prev => ({ ...prev, [loadingKey]: null }));
        }, 5000);
      }
    } catch (error) {
      console.error(`Error deleting ${section === 'team' ? 'team member' : 'testimonial'}:`, error);
      setError(prev => ({
        ...prev,
        [section === 'team' ? 'teamMember' : 'testimonialItem']: 
          error.response?.data?.message || error.message || `Failed to delete ${section === 'team' ? 'team member' : 'testimonial'}`
      }));
      
      // Log more detailed error information
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        console.error('Error response headers:', error.response.headers);
      } else if (error.request) {
        // The request was made but no response was received
        console.error('Error request:', error.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Error message:', error.message);
      }
    } finally {
      setLoading(prev => ({ 
        ...prev, 
        [section === 'team' ? 'teamMember' : 'testimonialItem']: false 
      }));
    }
  };

  // List of common FontAwesome icons for the dropdown
  const commonIcons = [
    { name: 'faAward', label: 'Award' },
    { name: 'faUsers', label: 'Users' },
    { name: 'faHome', label: 'Home' },
    { name: 'faHandshake', label: 'Handshake' },
    { name: 'faStar', label: 'Star' },
    { name: 'faCheck', label: 'Check' },
    { name: 'faHeart', label: 'Heart' },
    { name: 'faGem', label: 'Gem' },
    { name: 'faLightbulb', label: 'Lightbulb' },
    { name: 'faShieldAlt', label: 'Shield' },
    { name: 'faComments', label: 'Comments' },
    { name: 'faThumbsUp', label: 'Thumbs Up' },
    { name: 'faClock', label: 'Clock' },
    { name: 'faMoneyBillWave', label: 'Money' },
    { name: 'faChartLine', label: 'Chart' },
    { name: 'faBullseye', label: 'Target' },
    { name: 'faTools', label: 'Tools' },
    { name: 'faKey', label: 'Key' },
    { name: 'faBuilding', label: 'Building' },
    { name: 'faMapMarkerAlt', label: 'Location' },
    { name: 'faPhone', label: 'Phone' },
    { name: 'faEnvelope', label: 'Email' },
    { name: 'faLock', label: 'Lock' },
    { name: 'faSearch', label: 'Search' },
    { name: 'faCalendarAlt', label: 'Calendar' },
    { name: 'faRocket', label: 'Rocket' },
    { name: 'faBriefcase', label: 'Briefcase' },
    { name: 'faGlobe', label: 'Globe' },
    { name: 'faLeaf', label: 'Leaf' },
    { name: 'faTrophy', label: 'Trophy' }
  ];

  // Helper function to get the icon object from its name
  const getIconByName = (iconName) => {
    return solidIcons[iconName];
  };

  // Render values editor component
  const renderValuesEditor = () => {
    return (
      <Card className="content-card mt-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <Card.Title>Values</Card.Title>
            <Button 
              variant="success" 
              onClick={() => {
                const newValues = [...valuesData.values, {
                  title: 'New Value',
                  icon: 'faStar',
                  description: 'Description of this value'
                }];
                setValuesData({...valuesData, values: newValues});
              }}
            >
              <FaPlus className="me-2" />
              Add Value
            </Button>
          </div>
          
          {valuesData.values && valuesData.values.length > 0 ? (
            <Table responsive>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Icon</th>
                  <th>Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {valuesData.values.map((value, index) => (
                  <tr key={index}>
                    <td>
                      <Form.Control
                        type="text"
                        value={value.title || ''}
                        onChange={(e) => {
                          const updatedValues = [...valuesData.values];
                          updatedValues[index] = {...updatedValues[index], title: e.target.value};
                          setValuesData({...valuesData, values: updatedValues});
                        }}
                        placeholder="Value Title"
                      />
                    </td>
                    <td>
                      <InputGroup>
                        <InputGroup.Text>
                          {value.icon && (
                            <FontAwesomeIcon 
                              icon={getIconByName(value.icon)} 
                              style={{ width: '16px', height: '16px' }} 
                            />
                          )}
                        </InputGroup.Text>
                        <Form.Select
                          value={value.icon || ''}
                          onChange={(e) => {
                            const updatedValues = [...valuesData.values];
                            updatedValues[index] = {...updatedValues[index], icon: e.target.value};
                            setValuesData({...valuesData, values: updatedValues});
                          }}
                        >
                          <option value="">Select an icon</option>
                          {commonIcons.map((icon) => (
                            <option key={icon.name} value={icon.name}>
                              {icon.label}
                            </option>
                          ))}
                        </Form.Select>
                      </InputGroup>
                    </td>
                    <td>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={value.description || ''}
                        onChange={(e) => {
                          const updatedValues = [...valuesData.values];
                          updatedValues[index] = {...updatedValues[index], description: e.target.value};
                          setValuesData({...valuesData, values: updatedValues});
                        }}
                        placeholder="Value Description"
                      />
                    </td>
                    <td>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => {
                          const updatedValues = valuesData.values.filter((_, i) => i !== index);
                          setValuesData({...valuesData, values: updatedValues});
                        }}
                      >
                        <FaTrash />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <Alert variant="info">
              No values added yet. Click the "Add Value" button to add your first value.
            </Alert>
          )}
        </Card.Body>
      </Card>
    );
  };

  const renderSectionForm = (section, data, setData) => (
    <Card className="content-card mt-4">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <Card.Title>{section.charAt(0).toUpperCase() + section.slice(1)} Section</Card.Title>
          <Button 
            variant="primary" 
            onClick={() => handleSave(section)}
            disabled={loading[section]}
          >
            {loading[section] ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              <>
                <FaSave className="me-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
        
        {error[section] && (
          <Alert variant="danger" className="mb-4">
            {error[section]}
          </Alert>
        )}
        
        {success[section] && (
          <Alert variant="success" className="mb-4">
            {success[section]}
          </Alert>
        )}
        
        <Form onSubmit={(e) => e.preventDefault()}>
          <Row>
            <Col md={8}>
              <Form.Group className="mb-4">
                <Form.Label>Header</Form.Label>
                <Form.Control
                  type="text"
                  value={data.header}
                  onChange={(e) => setData({ ...data, header: e.target.value })}
                  placeholder="Enter header text"
                  disabled={loading[section]}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>Paragraph</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={data.paragraph}
                  onChange={(e) => setData({ ...data, paragraph: e.target.value })}
                  placeholder="Enter paragraph text"
                  disabled={loading[section]}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-4">
                <Form.Label>Image</Form.Label>
                <div className="image-upload-container">
                  <div className="current-image">
                    <img src={data.img} alt={`${section} preview`} className="img-preview" />
                  </div>
                  <div className="upload-button-container">
                    <label className={`btn btn-outline-primary w-100 ${loading[section] ? 'disabled' : ''}`}>
                      {loading[section] ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <FaUpload className="me-2" />
                          Upload New Image
                        </>
                      )}
                      <Form.Control
                        type="file"
                        accept="image/*"
                        className="d-none"
                        onChange={(e) => handleImageUpload(section, null, e)}
                        disabled={loading[section]}
                      />
                    </label>
                  </div>
                </div>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Card.Body>
    </Card>
  );

  const renderTable = (section, data, setData) => {
    const loadingKey = section === 'team' ? 'teamMember' : 'testimonialItem';
    
    return (
      <Card className="content-card mt-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <Card.Title>{section === 'team' ? 'Team Members' : 'Testimonials'}</Card.Title>
            <Button 
              variant="primary" 
              onClick={() => handleAddItem(section)}
              disabled={loading[loadingKey]}
            >
              {loading[loadingKey] && !loading[loadingKey + 'Delete'] ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Adding...
                </>
              ) : (
                <>
                  <FaPlus className="me-2" />
                  Add {section === 'team' ? 'Member' : 'Testimonial'}
                </>
              )}
            </Button>
          </div>
          
          {error[loadingKey] && (
            <Alert variant="danger" className="mb-4">
              {error[loadingKey]}
            </Alert>
          )}
          
          {success[loadingKey] && (
            <Alert variant="success" className="mb-4">
              {success[loadingKey]}
            </Alert>
          )}
          
          <Table responsive>
            <thead>
              <tr>
                <th>Image</th>
                <th>Name</th>
                <th>Position</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(section === 'team' ? data.members : data.testimonials).map((item) => (
                <tr key={item.id}>
                  <td>
                    <div className="table-image-container">
                      <img src={item.img} alt={item.name} className="table-image" />
                      <label className={`image-upload-overlay ${loading[loadingKey] ? 'disabled' : ''}`}>
                        {loading[loadingKey] ? <Spinner animation="border" size="sm" /> : <FaUpload />}
                        <Form.Control
                          type="file"
                          accept="image/*"
                          className="d-none"
                          onChange={(e) => handleImageUpload(section, item.id, e)}
                          disabled={loading[loadingKey]}
                        />
                      </label>
                    </div>
                  </td>
                  <td>
                    <Form.Control
                      type="text"
                      value={item.name}
                      onChange={(e) => {
                        const newItems = section === 'team' ? 
                          data.members.map(m => m.id === item.id ? {...m, name: e.target.value} : m) :
                          data.testimonials.map(t => t.id === item.id ? {...t, name: e.target.value} : t);
                        setData({
                          ...data,
                          [section === 'team' ? 'members' : 'testimonials']: newItems
                        });
                      }}
                      placeholder="Enter name"
                      disabled={loading[loadingKey]}
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="text"
                      value={item.position}
                      onChange={(e) => {
                        const newItems = section === 'team' ?
                          data.members.map(m => m.id === item.id ? {...m, position: e.target.value} : m) :
                          data.testimonials.map(t => t.id === item.id ? {...t, position: e.target.value} : t);
                        setData({
                          ...data,
                          [section === 'team' ? 'members' : 'testimonials']: newItems
                        });
                      }}
                      placeholder="Enter position"
                      disabled={loading[loadingKey]}
                    />
                  </td>
                  <td>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={item.paragraph}
                      onChange={(e) => {
                        const newItems = section === 'team' ?
                          data.members.map(m => m.id === item.id ? {...m, paragraph: e.target.value} : m) :
                          data.testimonials.map(t => t.id === item.id ? {...t, paragraph: e.target.value} : t);
                        setData({
                          ...data,
                          [section === 'team' ? 'members' : 'testimonials']: newItems
                        });
                      }}
                      placeholder="Enter description"
                      disabled={loading[loadingKey]}
                    />
                  </td>
                  <td>
                    <div className="action-buttons">
                      <Button 
                        variant="danger" 
                        size="sm"
                        onClick={() => handleDeleteItem(section, item.id)}
                        disabled={loading[loadingKey]}
                      >
                        {loading[loadingKey] ? <Spinner animation="border" size="sm" /> : <FaTrash />}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          
          {(section === 'team' ? data.members : data.testimonials).length > 0 && (
            <div className="d-flex justify-content-end mt-4">
              <Button 
                variant="primary" 
                onClick={() => handleSave(section)}
                disabled={loading[section]}
              >
                {loading[section] ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FaSave className="me-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  return (
    <div className="about-management">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="page-title">About Page Management</h2>
      </div>

      <Nav variant="tabs" activeKey={key} onSelect={(k) => setKey(k)}>
        <Nav.Item>
          <Nav.Link eventKey="about">About Section</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="values">Values Section</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="team">Team Section</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="testimonials">Testimonials Section</Nav.Link>
        </Nav.Item>
      </Nav>

      {key === 'about' && renderSectionForm('about', aboutData, setAboutData)}
      {key === 'values' && (
        <>
          {renderSectionForm('values', valuesData, setValuesData)}
          {renderValuesEditor()}
        </>
      )}
      {key === 'team' && (
        <>
          {renderSectionForm('team', teamData, setTeamData)}
          {renderTable('team', teamData, setTeamData)}
        </>
      )}
      {key === 'testimonials' && (
        <>
          {renderSectionForm('testimonials', testimonialData, setTestimonialData)}
          {renderTable('testimonials', testimonialData, setTestimonialData)}
        </>
      )}
    </div>
  );
};

export default AboutManagement;
